/**
 * Registry sectionKey → loader de componente con import() dinámico.
 *
 * CRÍTICO: cada valor ES una función que retorna un import() dinámico —
 * nunca el componente directo. Esto garantiza que Vite genere un chunk
 * separado por panel y que ningún panel entre en el entry bundle.
 *
 * Como los componentes usan named exports (no default), el loader los
 * envuelve en { default: ... } para cumplir el contrato de React.lazy.
 *
 * sectionKey que viene del endpoint sin entrada aquí → se ignora
 * silenciosamente (filtrado en useSections).
 */

import type { ComponentType } from "react";

// biome-ignore lint/suspicious/noExplicitAny: el registry es genérico por diseño — las props concretas las maneja el caller
export type SectionLoader = () => Promise<{ default: ComponentType<any> }>;

// `satisfies` valida que todos los valores son SectionLoader pero deja que TS
// infiera el tipo literal de las claves, evitando que el acceso por clave
// conocida retorne `SectionLoader | undefined`.
export const sectionRegistry = {
  resumen: () =>
    import("../../components/dashboard/Resumen").then((m) => ({
      default: m.Resumen,
    })),

  er: () =>
    import("../../components/dashboard/ERTable").then((m) => ({
      default: m.ERTable,
    })),

  centros: () =>
    import("../../components/dashboard/CCGrid").then((m) => ({
      default: m.CCGrid,
    })),

  rubros: () =>
    import("../../components/dashboard/RubrosTable").then((m) => ({
      default: m.RubrosTable,
    })),

  ventas: () =>
    import("../../components/dashboard/TransactionsTable").then((m) => ({
      default: m.TransactionsTable,
    })),

  compras: () =>
    import("../../components/dashboard/TransactionsTable").then((m) => ({
      default: m.TransactionsTable,
    })),
} satisfies Record<string, SectionLoader>;

/**
 * Labels del menú por sectionKey. En código (no en DB) porque es UI
 * de la consultora — no configurable por tenant.
 * sectionKey sin label aquí → se usa humanize(key) como fallback.
 */
export const sectionLabels: Record<string, string> = {
  resumen: "Resumen",
  er: "Estado de Resultados",
  centros: "Centros de Costos",
  rubros: "Composición Rubros",
  ventas: "Ventas",
  compras: "Compras",
};

/**
 * Fallback humanizado para sectionKeys desconocidos.
 * "ventas-extranjeras" → "Ventas extranjeras"
 */
export function humanizeKey(key: string): string {
  return key.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function getSectionLabel(key: string): string {
  return sectionLabels[key] ?? humanizeKey(key);
}
